import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, FileSpreadsheet, Loader2, Paperclip, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

const save = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};
const day = () => new Date().toLocaleDateString("en-CA");

// The app's own spreadsheets, one tap to attach.
const APP_SHEETS = [
  { id: "sku-notes", label: "SKU counts & notes", url: "/inventory/sku-notes/export", name: () => `snaplist-sku-notes-${day()}.csv`, owner: true },
  { id: "research", label: "Research history", url: "/research-export", params: { format: "csv" }, name: () => `snaplist-research-${day()}.csv` },
];

async function upload(file) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const { data } = await api.post("/messages/csv", fd);
  return data;
}

// "Attach CSV" for a message: a file from the phone/computer or one of the app's spreadsheets.
export function CsvPicker({ value, onChange, owner }) {
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const run = async (make) => {
    setBusy(true);
    try { onChange(await upload(await make())); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't attach that spreadsheet"); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const fromApp = (s) => run(async () => {
    const { data } = await api.get(s.url, { params: s.params, responseType: "blob", fresh: true, timeout: 120000 });
    return new File([data], s.name(), { type: "text/csv" });
  });
  if (value) {
    return (
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200" data-testid="csv-attached">
        <FileSpreadsheet className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{value.name}</span> · {value.rows} row{value.rows === 1 ? "" : "s"}
        <button type="button" onClick={() => onChange(null)} aria-label="Remove spreadsheet" className="text-emerald-700 hover:text-rose-600" data-testid="csv-remove"><X className="h-3.5 w-3.5" /></button>
      </span>
    );
  }
  return (
    <>
      <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files[0] && run(async () => e.target.files[0])} data-testid="csv-file-input" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={busy} data-testid="csv-attach"
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition-colors hover:border-[#0053DA] hover:text-[#0053DA] disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} Attach CSV
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem onSelect={() => input.current?.click()} data-testid="csv-attach-file"><Paperclip className="mr-2 h-4 w-4" /> Choose a CSV file</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">From SnapList</DropdownMenuLabel>
          {APP_SHEETS.filter((s) => owner || !s.owner).map((s) => (
            <DropdownMenuItem key={s.id} onSelect={() => fromApp(s)} data-testid={`csv-attach-${s.id}`}><FileSpreadsheet className="mr-2 h-4 w-4" /> {s.label}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

// A spreadsheet inside a message: preview as a table, or download it.
export function CsvChip({ csv, dark }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  if (!csv) return null;
  const open = async () => {
    setBusy("view");
    try { setView((await api.get(`/messages/csv/${csv.id}/preview`)).data); } catch { toast.error("Couldn't open the spreadsheet"); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("dl");
    try { save((await api.get(`/messages/csv/${csv.id}`, { responseType: "blob", fresh: true })).data, csv.name); } catch { toast.error("Couldn't download the spreadsheet"); } finally { setBusy(""); }
  };
  const btn = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${dark ? "bg-white/15 text-white hover:bg-white/25" : "bg-white text-[#0053DA] ring-1 ring-slate-200 hover:bg-blue-50"}`;
  return (
    <div className={`mt-1.5 rounded-xl px-2.5 py-2 ${dark ? "bg-white/10" : "bg-emerald-50 ring-1 ring-emerald-200"}`} data-testid={`csv-chip-${csv.id}`}>
      <p className={`flex items-center gap-1.5 text-xs font-semibold ${dark ? "text-white" : "text-emerald-900"}`}><FileSpreadsheet className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{csv.name}</span></p>
      <p className={`text-[10px] ${dark ? "text-white/70" : "text-emerald-700"}`}>{csv.rows} row{csv.rows === 1 ? "" : "s"}</p>
      <div className="mt-1 flex gap-1.5">
        <button type="button" onClick={open} disabled={!!busy} className={btn} data-testid={`csv-preview-${csv.id}`}>{busy === "view" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />} Preview</button>
        <button type="button" onClick={download} disabled={!!busy} className={btn} data-testid={`csv-download-${csv.id}`}>{busy === "dl" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Download</button>
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-3xl overflow-hidden rounded-xl" data-testid="csv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> {view?.name}</DialogTitle>
            <DialogDescription>{view && (view.total > view.rows.length ? `First ${view.rows.length} of ${view.total} rows` : `${view.total} row${view.total === 1 ? "" : "s"}`)}</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200" data-testid="csv-preview-table">
              <table className="min-w-full text-left text-xs">
                <thead className="sticky top-0 bg-slate-100"><tr>{view.header.map((h, i) => <th key={i} className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-700">{h}</th>)}</tr></thead>
                <tbody>{view.rows.map((r, i) => <tr key={i} className="border-t border-slate-100 odd:bg-white even:bg-slate-50">{view.header.map((_, j) => <td key={j} className="whitespace-nowrap px-2 py-1 text-slate-800">{r[j] ?? ""}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}
          <button type="button" onClick={download} className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0053DA] px-3 py-1.5 text-xs font-semibold text-white" data-testid="csv-preview-download"><Download className="h-3.5 w-3.5" /> Download {view?.name}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
